import {
  getMagicBrushError,
  hasMagicBrushError,
  Image,
  ProcessingError,
  ProcessingState,
  ResultVariant,
} from "@/modules/internal_api/image";
import Client from "@/modules/internal_api/client";
import { useEditorStore } from "@/stores/editor_store";
import { getNestedKey } from "@/modules/utils";

export const usePoll = () => {
  const store = useEditorStore();
  const timeouts: Map<string, any> = new Map();

  const poll = async (
    image: Image,
    variant: ResultVariant,
    handleProcessingError: (processingError: ProcessingError) => void
  ): Promise<Image> => {
    const stateKey: string = `${variant}.state`;
    const nextPollKey: string = `${variant}.nextFetchIn`;
    const timeoutKey: string = `${variant}.${image.meta.id}.timeout`;

    const executePoll = async (image, resolve, reject): Promise<void> => {
      const updatedImage: Image = await Client.getImage(image.meta.id);

      const isMagicBrushVariant = [ResultVariant.MagicBrushPreview].includes(variant);

      if (isMagicBrushVariant && hasMagicBrushError(updatedImage)) {
        const processingError: ProcessingError = getMagicBrushError(updatedImage);
        store.clearLines();
        handleProcessingError(processingError);
        store.selectedImage.magicBrushPreviewResult.state = ProcessingState.Error;
        return;
      }

      store.updateImage(updatedImage);

      const isMagicBackgroundVariant = variant === ResultVariant.MagicBackgroundPreview;
      const lastGeneration = Array.from(updatedImage.magicBackgroundResults.entries()).pop()?.[1];

      const state = isMagicBackgroundVariant
        ? lastGeneration?.magicBackgroundPreviewResult.state
        : (getNestedKey(updatedImage, stateKey) as ProcessingState);

      const isFinished = [ProcessingState.Finished, ProcessingState.Error].includes(state);

      if (isFinished === true) {
        clearTimeout(timeouts.get(timeoutKey));
        timeouts.delete(timeoutKey);
        return resolve(updatedImage);
      } else {
        const nextFetchIn = isMagicBackgroundVariant
          ? lastGeneration?.magicBackgroundPreviewResult.nextFetchIn
          : getNestedKey(updatedImage, nextPollKey);
        const timeout = setTimeout(async () => {
          executePoll(updatedImage, resolve, reject);
        }, nextFetchIn);

        timeouts.set(timeoutKey, timeout);
      }
    };

    return new Promise((resolve, reject) => {
      executePoll(image, resolve, reject);
    });
  };

  return { poll };
};
